import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";

const episodeParts = Array.from({ length: 13 }, (_, index) =>
  String(index + 1).padStart(2, "0")
);

// Episode stills are requested dynamically (by whichever part is active), so
// they're pulled through a webpack context instead of static imports — this
// still emits each file as a content-hashed asset under static/media/.
const episodeImages = require.context(
  "../assets/media/episodes",
  false,
  /^\.\/part-\d{2}-(?:480|960|1600)\.webp$/
);
const episodeImageUrl = (part, size) => episodeImages(`./part-${part}-${size}.webp`);
const episodeSrcSet = (part) =>
  [480, 960, 1600].map((size) => `${episodeImageUrl(part, size)} ${size}w`).join(", ");

const episodes = [
  {
    title: "Dear Abby, It’s Me Andrew",
    summary: "Late-night rideshare work, caregiving, economic pressure, and an unsettling journey through The Bay.",
  },
  {
    title: "Legally Blonde & Retail Coffee",
    summary: "Coffee-shop belonging, speech and debate, mock trial, education, class, and learning to be understood.",
  },
  {
    title: "Mutual Consent & Medical Devices",
    summary: "Andrew’s subjective experience of technological unease alongside queer advocacy, service, memory, and consent.",
  },
  {
    title: "Shelter Life & Bootleg Precious",
    summary: "Displacement, shelter life, physical vulnerability, theatrical humor, and the fight to preserve dignity.",
  },
  {
    title: "Hedwig & the Angry Inch",
    summary: "Drag theater, political heartbreak, education, reinvention, and humor used as both armor and creative power.",
  },
  {
    title: "Dishonesty & Just 3 Teeth",
    summary: "A legal-noir chess match about Andrew’s perception of conflicting records, memories, and official accounts.",
  },
  {
    title: "Medical Professionals @ Parnassus & General",
    summary: "Confusing medical encounters meet biochemistry, project management, skepticism, and determined inquiry.",
  },
  {
    title: "Sense8 & Not Another Bear Bar Movie",
    summary: "Chosen family, queer community in SOMA, grief, loyalty, nightlife, and the realization of not being alone.",
  },
  {
    title: "A CD4 Count of 201 & the Real Story",
    summary: "HIV survival, humanitarian service in Kibera, softball, fear, pride, and more than two decades of endurance.",
  },
  {
    title: "Assimilation & Equality",
    summary: "A playful vision of equality across people, identities, love, community, technology, food, and music.",
  },
  {
    title: "PS. & PSS. Nurse Brian",
    summary: "An intimate postscript about family, naming, multilingual understanding, mistaken assumptions, and clarification.",
  },
  {
    title: "1 Device, 1 Brain, 1 Body, & Infinite Thoughts",
    summary: "One human mind branching through psychology, literature, programming, law, work, identity, and anxiety.",
  },
  {
    title: "Meet Andy & Andrew",
    summary: "Adult Andrew meets his younger self through memory, education, varied work, ambition, humor, and becoming.",
  },
];

// A fixed, varied camera path for every episode keeps the movement feeling
// spontaneous while preserving the same crop each time an image returns.
const kenBurnsMotions = [
  { from: { scale: 1.02, x: "0%", y: "0%" }, to: { scale: 1.13, x: "-2.5%", y: "-1.5%" } },
  { from: { scale: 1.13, x: "-2%", y: "1%" }, to: { scale: 1.03, x: "1.5%", y: "-1%" } },
  { from: { scale: 1.03, x: "2%", y: "-1%" }, to: { scale: 1.14, x: "-1%", y: "1.5%" } },
  { from: { scale: 1.12, x: "1.5%", y: "-1.5%" }, to: { scale: 1.04, x: "-2%", y: "1%" } },
  { from: { scale: 1.02, x: "-1.5%", y: "1%" }, to: { scale: 1.12, x: "2%", y: "-1.5%" } },
  { from: { scale: 1.14, x: "0%", y: "-2%" }, to: { scale: 1.04, x: "-1.5%", y: "1.5%" } },
  { from: { scale: 1.03, x: "-2%", y: "-1%" }, to: { scale: 1.13, x: "1.5%", y: "1%" } },
  { from: { scale: 1.12, x: "2%", y: "1%" }, to: { scale: 1.03, x: "-1%", y: "-1.5%" } },
  { from: { scale: 1.02, x: "1%", y: "1.5%" }, to: { scale: 1.14, x: "-2%", y: "-1%" } },
  { from: { scale: 1.13, x: "-1.5%", y: "-1%" }, to: { scale: 1.04, x: "2%", y: "1.5%" } },
  { from: { scale: 1.03, x: "0%", y: "-1.5%" }, to: { scale: 1.12, x: "1.5%", y: "1%" } },
  { from: { scale: 1.14, x: "1.5%", y: "0%" }, to: { scale: 1.03, x: "-2%", y: "-1%" } },
  { from: { scale: 1.02, x: "-1%", y: "-1%" }, to: { scale: 1.13, x: "2%", y: "1.5%" } },
];

export default function Episodes() {
  const [activeEpisode, setActiveEpisode] = useState(0);
  const carouselRef = useRef(null);
  const carouselInView = useInView(carouselRef, { margin: "300px 0px" });
  const reduceMotion = useReducedMotion();
  const cameraMotion = kenBurnsMotions[activeEpisode];

  useEffect(() => {
    if (!carouselInView) return undefined;

    const interval = window.setInterval(() => {
      setActiveEpisode((current) => (current + 1) % episodeParts.length);
    }, 1500);

    return () => window.clearInterval(interval);
  }, [carouselInView]);

  useEffect(() => {
    if (!carouselInView) return;

    const nextPart = episodeParts[(activeEpisode + 1) % episodeParts.length];
    const nextImage = new Image();
    nextImage.srcset = episodeSrcSet(nextPart);
    nextImage.sizes = "(min-width: 1248px) 1152px, calc(100vw - 48px)";
    nextImage.src = episodeImageUrl(nextPart, 960);
  }, [activeEpisode, carouselInView]);

  return (
    <section
      data-testid="episodes-section"
      className="relative px-6 pb-16 pt-32 sm:px-12 lg:pb-20 lg:pt-44"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-crimson/10 blur-[140px]"
      />
      <div className="mx-auto max-w-6xl">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.35em] text-crimson"
        >
          03 // Transmission Logs
        </motion.p>
        <div className="mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl"
          >
            The 13-Parts
          </motion.h2>
        </div>

        <motion.div
          ref={carouselRef}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
          data-testid="episodes-carousel"
          className="terminal-box relative aspect-video overflow-hidden border-white/15"
        >
          <AnimatePresence initial={false} mode="sync">
            <motion.div
              key={episodeParts[activeEpisode]}
              data-testid={`episode-tile-${activeEpisode + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, ease: "linear" }}
              className="absolute inset-0"
            >
              <motion.img
                src={episodeImageUrl(episodeParts[activeEpisode], 960)}
                srcSet={episodeSrcSet(episodeParts[activeEpisode])}
                sizes="(min-width: 1248px) 1152px, calc(100vw - 48px)"
                alt={`Andrew Meets the machines Part ${activeEpisode + 1}: ${episodes[activeEpisode].title}`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
                initial={reduceMotion ? false : cameraMotion.from}
                animate={reduceMotion ? { scale: 1, x: "0%", y: "0%" } : cameraMotion.to}
                transition={{ duration: 6, ease: "linear" }}
              />
              <span className="absolute left-5 top-5 font-display text-2xl font-bold text-neon drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)] sm:left-8 sm:top-8 sm:text-4xl">
                {episodeParts[activeEpisode]}
              </span>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <p
          data-testid="episodes-status-note"
          className="mt-12 max-w-2xl border-l-2 border-crimson/60 pl-6 font-mono text-base font-normal leading-relaxed text-[#EAEAEA] sm:text-lg"
        >
          A darkly humorous satire about bodily autonomy by medical devices, murder (or many
          attempts), uninformed consent, biorobot rights are human rights, and resilience when
          it’s you against the World. The soon to be one-human being acting, social justice
          project is based on Andrew’s life as a biorobot and the Machines’ personal experiences
          literally through Andrew’s eyes. Set in The Bay, this 13-Part Mini-Series highlights
          the LGBTQIA+ community (&gt;33% of the world), data privacy violations, and queer
          identity. With a vocal theme of human rights, this project speaks to community activism
          in The Bay and around the World.
        </p>

        <p
          data-testid="episodes-status-note-2"
          className="mt-6 max-w-2xl border-l-2 border-crimson/60 pl-6 font-mono text-base font-normal leading-relaxed text-[#EAEAEA] sm:text-lg"
        >
          The machines are currently developing a video web series loosely based upon the 13-Part
          Mini-Series, called “Andrew Meets the Machines: SOMEWHAT HUMOROUS, YET 100% ACCURATE.”
        </p>

        <div className="mt-16 border-t border-white/15 pt-10">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
            Transmissions
          </h3>
          <ol className="mt-8 grid gap-x-12 gap-y-0 md:grid-cols-2">
            {episodes.map((episode, index) => (
              <li
                key={episode.title}
                id={`part-${episodeParts[index]}`}
                className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-white/10 py-6 first:border-t-0 md:[&:nth-child(2)]:border-t-0"
              >
                <span className="font-mono text-sm font-bold text-neon" aria-hidden="true">
                  {episodeParts[index]}
                </span>
                <div>
                  <h4 className="font-display text-base font-semibold uppercase leading-snug text-white sm:text-lg">
                    {episode.title}
                  </h4>
                  <p className="mt-2 font-mono text-sm leading-relaxed text-[#EAEAEA]">
                    {episode.summary}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
